/**
 * Phase 299.a — Dynamic Favicon Notification Badge
 * =================================================
 *
 * The OS-level `navigator.setAppBadge()` API renders a dull translucent-gray
 * dot on Windows / macOS — which dies in dark mode and gets lost on busy
 * taskbars. To force a screaming-red attention dot, we render the badge
 * ourselves onto an HTML5 <canvas> and swap the <link rel="icon"> href.
 *
 * The native badge is still fired in parallel for mobile (Android home-screen
 * counter, iOS PWA dock badge — both render those nicely).
 *
 * Design choices:
 *   • Red circle (#ef4444) bottom-right (clears the academy mark on the
 *     left half of the logo so it doesn't obscure the brand).
 *   • White bold count with auto-shrinking font for 2-digit values.
 *   • Cap visual at "9+" once count > 9 to keep the dot legible at 32px.
 *   • Base logo is cached after first load — zero network on subsequent
 *     badge updates.
 *
 * Usage:
 *   import { updateFaviconBadge } from '@/lib/faviconBadge';
 *   updateFaviconBadge(unreadCount);
 */

const FAVICON_SIZE = 64;            // upscale target — crisper on retina taskbars
const BADGE_RADIUS = 20;             // Phase 324b — bumped from 18 → 20 for higher visibility
// Phase 324b — vibrant rose→amber gradient + white halo. Replaces the flat
// red-500 dot so the badge POPS on dark taskbars and busy browser tabs.
const BADGE_GRADIENT_FROM = '#F43F5E'; // rose-500 (matches PWA theme_color)
const BADGE_GRADIENT_TO   = '#F97316'; // orange-500
const BADGE_BORDER = '#ffffff';
const BADGE_TEXT   = '#ffffff';
const BADGE_GLOW   = 'rgba(244, 63, 94, 0.55)';

let _baseImage = null;               // memoised <img> with the academy logo
let _baseImageLoading = null;        // in-flight load promise (dedupes calls)
let _originalHref = null;            // cached so we can restore on count=0
let _lastAppliedCount = -1;          // skip redundant redraws

function _getFaviconLink() {
  if (typeof document === 'undefined') return null;
  // Look for the first <link rel="icon"> — the prod template registers one
  // with href '/logo192.png'. We DO NOT want to grab apple-touch-icon since
  // iOS reads that from its own cache and we can't update it dynamically.
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    document.head.appendChild(link);
  }
  if (_originalHref === null) _originalHref = link.getAttribute('href') || '/logo192.png';
  return link;
}

function _loadBaseImage() {
  if (_baseImage) return Promise.resolve(_baseImage);
  if (_baseImageLoading) return _baseImageLoading;
  _baseImageLoading = new Promise((resolve, reject) => {
    const link = _getFaviconLink();
    const src = (link && link.getAttribute('href')) || '/logo192.png';
    const img = new Image();
    img.crossOrigin = 'anonymous';   // allow canvas export (same-origin assets)
    img.onload = () => { _baseImage = img; resolve(img); };
    img.onerror = (err) => { _baseImageLoading = null; reject(err); };
    img.src = src;
  });
  return _baseImageLoading;
}

function _restoreOriginal() {
  const link = _getFaviconLink();
  if (link && _originalHref) link.setAttribute('href', _originalHref);
}

/**
 * Replace the favicon with a logo + red badge composite. When `count` is
 * 0 or negative, restore the original logo. Silently no-ops on canvas /
 * cross-origin / SSR failure modes.
 */
export async function updateFaviconBadge(count) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const safe = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  if (safe === _lastAppliedCount) return;
  _lastAppliedCount = safe;

  if (safe === 0) { _restoreOriginal(); return; }

  let img;
  try { img = await _loadBaseImage(); }
  catch { return; /* network/CORS — leave the OS-level badge as fallback */ }

  let canvas;
  try {
    canvas = document.createElement('canvas');
    canvas.width = FAVICON_SIZE;
    canvas.height = FAVICON_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw the base logo, slightly scaled down so the badge has breathing room.
    ctx.clearRect(0, 0, FAVICON_SIZE, FAVICON_SIZE);
    ctx.drawImage(img, 0, 0, FAVICON_SIZE, FAVICON_SIZE);

    const cx = FAVICON_SIZE - BADGE_RADIUS - 2;
    const cy = FAVICON_SIZE - BADGE_RADIUS - 2;

    // Soft glow halo so the badge feels alive on dark taskbars.
    ctx.beginPath();
    ctx.arc(cx, cy, BADGE_RADIUS + 6, 0, Math.PI * 2);
    ctx.fillStyle = BADGE_GLOW;
    ctx.fill();

    // White outline ring (separates the badge from any logo background).
    ctx.beginPath();
    ctx.arc(cx, cy, BADGE_RADIUS + 3, 0, Math.PI * 2);
    ctx.fillStyle = BADGE_BORDER;
    ctx.fill();

    // Vibrant rose→amber gradient attention dot.
    const grad = ctx.createLinearGradient(cx - BADGE_RADIUS, cy - BADGE_RADIUS, cx + BADGE_RADIUS, cy + BADGE_RADIUS);
    grad.addColorStop(0, BADGE_GRADIENT_FROM);
    grad.addColorStop(1, BADGE_GRADIENT_TO);
    ctx.beginPath();
    ctx.arc(cx, cy, BADGE_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    // Count text — auto-shrink for 2-digit + cap at "9+".
    const label = safe > 9 ? '9+' : String(safe);
    const fontSize = label.length === 1 ? 24 : 19;
    ctx.fillStyle = BADGE_TEXT;
    ctx.font = `800 ${fontSize}px Arial, Helvetica, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx, cy + 1);   // +1 nudges optical center down

    const link = _getFaviconLink();
    if (link) link.setAttribute('href', canvas.toDataURL('image/png'));
  } catch { /* swallow — fall back to the OS-level badge */ }
}

/**
 * Mobile-OS-level badge (Android, iOS PWA). The native API renders fine
 * on phones — it's only desktop where the gray dot is dull. We fire BOTH
 * paths so every surface gets the best version of the badge.
 */
export function syncNativeBadge(count) {
  try {
    if (typeof navigator === 'undefined') return;
    const safe = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    if (typeof navigator.setAppBadge === 'function') {
      if (safe === 0) {
        if (typeof navigator.clearAppBadge === 'function') navigator.clearAppBadge();
        else navigator.setAppBadge(0);
      } else {
        navigator.setAppBadge(safe);
      }
      return;
    }
    // Service-worker path (existing PWA infra for the manifest install case).
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(
        safe === 0 ? { type: 'clear-badge' } : { type: 'set-badge', count: safe }
      );
    }
  } catch { /* best-effort */ }
}

/**
 * One-call helper used by NotificationBell — does BOTH the canvas favicon
 * paint AND the mobile-OS setAppBadge in parallel.
 */
export function syncAppBadgeAll(count) {
  // Fire and forget — these are independent best-effort updates.
  updateFaviconBadge(count);
  syncNativeBadge(count);
}
